// src/hooks/useANPRAlarm.ts

import { AlarmConfiguration } from 'domain/alarm-configuration'
import { useToast } from 'layout/context/toast-context'
import { useCallback, useState } from 'react'
import anprAlarmApiService from '../api/anpr-alarm.api'
import { AlarmQueryParams } from '../types/alarm-query-params'
import { APIAlarmCriteria } from '../types/api-alarm-criteria'
import { AxiosError } from 'axios'
import { ApiResponse } from 'http/types/api-response'
import { ApiErrorResponse } from 'http/types/api-error-response'
import { useLoading } from 'layout/context/loading-context'
import { AlarmOutput } from 'domain/alarm-output'

interface UseAlarmHookReturn {
  // Alarm Configuration operations
  alarmConfigurations: AlarmConfiguration[]
  totalConfigurations: number

  // Alarm Event operations
  alarmEvents: AlarmOutput[]
  totalEvents: number

  // Alarm Criteria
  anprAlarmCriteria: APIAlarmCriteria[]
  ahcrAlarmCriteria: APIAlarmCriteria[]
  fetchAlarmCriteria: () => Promise<void>

  // Common state

  // Alarm Configuration Actions
  fetchAlarmConfigurations: (params?: AlarmQueryParams) => Promise<void>
  createAlarmConfiguration: (
    clientId: string | null,
    data: AlarmConfiguration
  ) => Promise<AlarmConfiguration | null>
  deleteAlarmConfiguration: (id: string) => Promise<boolean>

  // Alarm Event Actions
  fetchAlarmEvents: (params?: AlarmQueryParams) => Promise<void>
}

export const useAlarm = (): UseAlarmHookReturn => {
  const { showSuccess, showError } = useToast()
  const [alarmConfiguration, setAlarmConfiguration] = useState<AlarmConfiguration[]>([])
  const [totalConfigurations, setTotalConfigurations] = useState<number>(0)
  const [alarmEvents, setAlarmEvents] = useState<AlarmOutput[]>([])
  const [totalEvents, setTotalEvents] = useState<number>(0)
  const [anprAlarmCriteria, setAnprAlarmCriteria] = useState<APIAlarmCriteria[]>([])
  const [ahcrAlarmCriteria, setAhcrAlarmCriteria] = useState<APIAlarmCriteria[]>([])

  const { startLoading, stopLoading } = useLoading()

  const fetchAlarmConfigurations = useCallback(
    async (params?: AlarmQueryParams): Promise<void> => {
      if (!params?.clientId) {
        return
      }

      startLoading('fetchAlarmConfigurations')

      await anprAlarmApiService.getAlarmConfigurations(params)
        .then((response) => {
          setAlarmConfiguration(response.responseObject)
          setTotalConfigurations(response.responseObject.length)
        })
        .catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setAlarmConfiguration([])
        })
        .finally(() => {
          stopLoading('fetchAlarmConfigurations')
        })
    }, [])

  const createAlarmConfiguration = useCallback(
    async (
      clientId: string | null,
      data: AlarmConfiguration
    ): Promise<AlarmConfiguration | null> => {

      startLoading('createAlarmConfiguration')
      const response = await anprAlarmApiService.createAlarmConfiguration(clientId, data)
        .then((data: ApiResponse<AlarmConfiguration>) => {
          const newAlarmConfiguration = data.responseObject as AlarmConfiguration

          setAlarmConfiguration((prevConfigs: AlarmConfiguration[]) => [
            ...prevConfigs,
            newAlarmConfiguration,
          ])

          showSuccess('Sucesso', 'Alarme criado com sucesso')

          return newAlarmConfiguration
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.responseObject?.errorMessage) {
            showError("Erro", err.responseObject.errorMessage)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          return null
        }).finally(() => {
          stopLoading('createAlarmConfiguration')
        })

      return response
    }, [])

  const deleteAlarmConfiguration = useCallback(
    async (id: string): Promise<boolean> => {

      startLoading('deleteAlarmConfiguration')
      const response = await anprAlarmApiService.deleteAlarmConfiguration(id)
        .then(() => {
          setAlarmConfiguration((prevConfigs) =>
            prevConfigs.filter((config) => config.id !== id)
          )

          showSuccess('Sucesso', 'Alarme excluído com sucesso')
          return true
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.responseObject?.errorMessage) {
            showError("Erro", err.responseObject.errorMessage)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          return false
        }).finally(() => {
          stopLoading('deleteAlarmConfiguration')
        })

      return response
    }, [])

  const fetchAlarmEvents = useCallback(
    async (params?: AlarmQueryParams): Promise<void> => {
      // Only proceed if params has clientId
      if (!params?.clientId) {
        return
      }

      startLoading('fetchAlarmEvents')

      await anprAlarmApiService.getAlarmEvents(params)
        .then((response) => {
          setAlarmEvents(response.responseObject)
          setTotalEvents(response.responseObject.length)
        })
        .catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.responseObject?.errorMessage) {
            showError("Erro", err.responseObject.errorMessage)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setAlarmEvents([])
        })
        .finally(() => {
          stopLoading('fetchAlarmEvents')
        })
    }, [])

  const fetchAlarmCriteria = useCallback(async (): Promise<void> => {
    startLoading('fetchAlarmCriteria')

    const ahcrResponse = anprAlarmApiService.getAHCRAlarmCriteria()
    const anprResponse = anprAlarmApiService.getANPRAlarmCriteria()

    await Promise.all([ahcrResponse, anprResponse])
      .then(([ahcrData, anprData]) => {
        setAhcrAlarmCriteria(ahcrData)
        setAnprAlarmCriteria(anprData)
      })
      .catch((error: AxiosError) => {
        const err = error.response?.data as ApiErrorResponse | undefined

        if (err?.error && err?.message) {
          showError(err.error, err.message)
        } else if (!error.response) {
          showError('Erro de conexão', 'Serviço temporariamente indisponível')
        }

      })
      .finally(() => {
        stopLoading('fetchAlarmCriteria')
      })

  }, [])

  return {
    // Alarm Configuration state
    alarmConfigurations: alarmConfiguration,
    totalConfigurations,

    // Alarm Event state
    alarmEvents,
    totalEvents,

    // Alarm Criteria
    anprAlarmCriteria,
    ahcrAlarmCriteria,
    fetchAlarmCriteria,

    // Alarm Configuration actions
    fetchAlarmConfigurations,
    createAlarmConfiguration,
    deleteAlarmConfiguration,

    // Alarm Event actions
    fetchAlarmEvents,
  }
}
